import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { useStudioDraft, type UseStudioDraft } from './useStudioDraft';
import { useStudioFileConnectors } from './useStudioFileConnectors';

interface StudioDraftContextValue {
  draft: UseStudioDraft;
  /** Source sub-step index (0 connect · 1 auth · 2 folder), persisted across pages. */
  srcSub: number;
  setSrcSub: (i: number) => void;
  /** Destination sub-step index. */
  destSub: number;
  setDestSub: (i: number) => void;
  orgId: any;
  /** File-only source/sink connector keys. */
  fileSources: string[];
  fileSinks: string[];
  /**
   * What the canvas create path (ByoSourceConfigureStep) has already persisted
   * for this draft. A ref, not state: it only exists so a retried "Continue"
   * (after a later step failed) resumes instead of POSTing a second
   * source/nexset. Lives here, above the <Form>, because the Form unmounts
   * steps on Back (SliderPage unmountOnExit) — a ref inside the step would be
   * lost and the next Continue would duplicate the source.
   */
  sourceCreateProgressRef: React.MutableRefObject<ByoSourceCreateProgress>;
  /** Same, for the destination step (ByoDestinationConfigureStep). */
  sinkCreateProgressRef: React.MutableRefObject<ByoSinkCreateProgress>;
}

export interface ByoSourceCreateProgress {
  sourceId: number | string | null;
  nexsetId: number | string | null;
  /** Identity of the inputs the source was created/last updated from. */
  inputsKey: string | null;
}

export interface ByoSinkCreateProgress {
  /** The source the sink was created for; progress for any other source is stale. */
  sourceId: number | string | null;
  sinkId: number | string | null;
  linked: boolean;
  inputsKey: string | null;
}

const StudioDraftContext = createContext<StudioDraftContextValue | null>(null);

export function useStudioDraftCtx(): StudioDraftContextValue {
  const ctx = useContext(StudioDraftContext);
  if (!ctx) throw new Error('useStudioDraftCtx must be used within <StudioDraftProvider>');
  return ctx;
}

/**
 * Shares the single BYO draft (+ sub-step nav + file-connector lists) across the
 * Source/Code/Destination Form pages. Loads vendors + credentials once so the
 * real SelectConnection/EnterCredential render.
 */
export function StudioDraftProvider({ children }: { children: React.ReactNode }) {
  // Some standalone hosts (e.g. ModalSourceForm.tsx) mount a component that
  // renders its own <StudioDraftProvider> with no ambient one above it, so
  // this hook call stays unconditional. But when this provider is itself
  // nested inside an ANCESTOR <StudioDraftProvider> (e.g. the canvas shell's
  // ByoSourceSetupForm rendered inside ByoCreateCanvas's provider), mounting a
  // second instance here shadows the outer one with a fresh, empty draft —
  // anything already written to the outer draft (like the canvas header's
  // "Flow name" input) becomes invisible to descendants, which read this
  // nested, never-written-to instance instead. Confirmed 2026-08-19.
  const ambient = useContext(StudioDraftContext);
  const draft = useStudioDraft();
  const [srcSub, setSrcSub] = useState(0);
  const [destSub, setDestSub] = useState(0);
  const { orgId, fileSources, fileSinks } = useStudioFileConnectors();
  const sourceCreateProgressRef = useRef<ByoSourceCreateProgress>({
    sourceId: null,
    nexsetId: null,
    inputsKey: null,
  });
  const sinkCreateProgressRef = useRef<ByoSinkCreateProgress>({
    sourceId: null,
    sinkId: null,
    linked: false,
    inputsKey: null,
  });

  const value = useMemo<StudioDraftContextValue>(
    () => ({
      draft,
      srcSub,
      setSrcSub,
      destSub,
      setDestSub,
      orgId,
      fileSources,
      fileSinks,
      sourceCreateProgressRef,
      sinkCreateProgressRef,
    }),
    [draft, srcSub, destSub, orgId, fileSources, fileSinks],
  );

  if (ambient) return <>{children}</>;

  return <StudioDraftContext.Provider value={value}>{children}</StudioDraftContext.Provider>;
}
